import React, { useCallback, useMemo } from 'react';
import { StampOptions } from '../StamperView';
import { ApprovalMap } from './__generated__/ApprovalMap';
import {
  EntityApprovalMap,
  EntityApprovalMap_entityApprovalMap_nodes,
} from './__generated__/EntityApprovalMap';
import { NodeView } from './NodeView';

// Import the HierarchyNode type from GenericHierarchyFlow
import { GenericHierarchyFlow } from '../PreviewApprovalHierarchyReactFlow';

import ENTITY_APPROVAL_MAP from './EntityApprovalMap.graphql';
import APPROVAL_MAP from './ApprovalMap.graphql';

export interface HierarchyNode
  extends EntityApprovalMap_entityApprovalMap_nodes {
  children?: HierarchyNode[];
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

interface ApprovalHierarchyProps {
  entityId: string;
  getApprovalType: (approvalType: string) => void;
  stampEnteredData?: (data: StampOptions) => void;
  isApprovalEntry?: boolean;
}

export const ApprovalHierarchy: React.FC<ApprovalHierarchyProps> = ({
  entityId,
  getApprovalType,
  stampEnteredData,
  isApprovalEntry,
}) => {
  // Select the appropriate query based on isApprovalEntry
  const query = isApprovalEntry ? APPROVAL_MAP : ENTITY_APPROVAL_MAP;
  // Memoize query variables to prevent unnecessary re-renders
  const queryVariables = useMemo(
    () => ({
      entityId,
      orderBy: ['SORT_ORDER_DESC'],
    }),
    [entityId]
  );

  // Memoize the getNodesFromData function with proper type narrowing
  const getNodesFromData = useCallback(
    (data: ApprovalMap | EntityApprovalMap) => {
      const nodes = isApprovalEntry
        ? (data as ApprovalMap).approvalMap?.nodes
        : (data as EntityApprovalMap).entityApprovalMap?.nodes;

      return nodes?.map((item) => ({
        ...item,
        children: [],
        level: item.level ?? 0,
      }));
    },
    [isApprovalEntry]
  );

  // Memoize the onNodeData callback with type assertion
  const onNodeData = useCallback(
    (nodeData: unknown) => {
      // Assert that nodeData is of type HierarchyNode (from ApprovalHierarchy)
      const typedNodeData = nodeData as HierarchyNode | undefined;
      if (typedNodeData?.approvalType) {
        getApprovalType(typedNodeData.approvalType);
      }
      if (typedNodeData && stampEnteredData) {
        const stampOptions: StampOptions = {
          _accountingStampDate: typedNodeData._accountingStampDate,
          _accountingStampTransactionReference:
            typedNodeData._accountingStampTransactionReference,
          _accountingStampUserName: typedNodeData._accountingStampUserName,
          _isAccountingEntryStampedComplete:
            typedNodeData._isAccountingEntryStampedComplete,
          _isTransactionCancelled: typedNodeData._isTransactionCancelled,
        };
        stampEnteredData(stampOptions);
      }
    },
    [getApprovalType, stampEnteredData]
  );
  const calculateHeight = () => {
    const viewportHeight = window.innerHeight;
    return viewportHeight * 0.8 - 170;
  };
  return (
    <GenericHierarchyFlow
      entityId={entityId}
      query={query}
      height={calculateHeight()}
      queryVariables={queryVariables}
      getNodesFromData={getNodesFromData}
      onNodeData={onNodeData}
      NodeViewComponent={(props) => (
        <NodeView
          data={props.data as unknown as HierarchyNode}
          key={props.data.id}
        />
      )}
    />
  );
};
